import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';

/**
 * "Ownership Relation Access" — tree-grouped master list + rooted-tree modal.
 *
 * Redesign (task 5): instead of one checkable row per FK field and a flat
 * inline "chain" list, we now:
 *   1. Group every fetched FK field into undirected connected components of
 *      the FK graph and render ONE navigation row per component, labeled by
 *      the component's ultimate root (owner) table(s). Rows have no checkbox
 *      — clicking one opens a modal.
 *   2. Render the component as a real rooted, branching tree in a large
 *      scrollable modal ("rich screen"). The root owner table sits on the
 *      left; every table that has an FK pointing at it branches out to the
 *      right, recursively. Each edge (a child table's FK → its parent) is
 *      independently enabled (▶) or disabled (✕) by clicking it, exactly as
 *      the old chain graph allowed.
 *
 * Two hard-won fixes from prior review rounds are preserved (see
 * `setFieldEnabled` and the auto-persist effect in `TreeModal`).
 *
 * `configuredFieldIds` (root-cause fix): a tree that has had every edge
 * explicitly disabled looks identical, from `selectedFieldIds` alone, to a
 * tree that was never opened — both are "zero of this tree's fields are
 * enabled". Telling them apart used to rely on a React state Set scoped to
 * one mount of this component, which is wiped by any navigation away and
 * back (or a reload), so a deliberately-emptied tree would silently get
 * re-defaulted to all-enabled and re-persisted. `configuredFieldIds` is the
 * durable, backend-persisted equivalent (`OrganizationGroup.
 * ownership_relation_configured_fields` on the Django model) — every field
 * that has ever been explicitly enabled OR disabled lives in it permanently,
 * so "configured" can be read straight from data instead of guessed from
 * component state. See `setFieldEnabled` and `enableFields` below, which
 * grow it, and `TreeModal`'s `compConfigured`, which reads it.
 */

// ---------------------------------------------------------------------------
// Pure graph helpers (module scope so they're trivially testable/deterministic)
// ---------------------------------------------------------------------------

/**
 * Build the undirected FK adjacency plus the set of "source" tables (any
 * table that is some field's table_name — i.e. it points away at something,
 * so it can never be an ultimate owner/root).
 */
function buildGraph(fields) {
    const sourceTables = new Set();
    const allTables = new Set();
    const adj = new Map();
    const ensure = (t) => {
        if (!adj.has(t)) adj.set(t, new Set());
        allTables.add(t);
    };
    for (const f of fields) {
        sourceTables.add(f.table_name);
        ensure(f.table_name);
        if (f.related_table_name) {
            ensure(f.related_table_name);
            adj.get(f.table_name).add(f.related_table_name);
            adj.get(f.related_table_name).add(f.table_name);
        }
    }
    return { sourceTables, allTables, adj };
}

/**
 * Connected components of the FK graph. Each returned component carries:
 *  - id: stable identity string (sorted table names)
 *  - tables: Set<string> of every table in the component
 *  - roots: string[] tables that are never any field's table_name (owners).
 *           Normally exactly one; may be 0 (inconsistent snapshot) or many.
 *  - fields: the fetched fields whose table_name is in this component
 */
function computeComponents(fields) {
    const { sourceTables, allTables, adj } = buildGraph(fields);
    const visited = new Set();
    const comps = [];
    for (const start of allTables) {
        if (visited.has(start)) continue;
        const tables = new Set();
        const stack = [start];
        visited.add(start);
        while (stack.length) {
            const cur = stack.pop();
            tables.add(cur);
            for (const nb of adj.get(cur) || []) {
                if (!visited.has(nb)) {
                    visited.add(nb);
                    stack.push(nb);
                }
            }
        }
        const roots = [...tables].filter((t) => !sourceTables.has(t));
        const compFields = fields.filter((f) => tables.has(f.table_name));
        comps.push({
            id: [...tables].sort().join('|'),
            tables,
            roots,
            fields: compFields,
        });
    }
    return comps;
}

function componentLabel(comp) {
    const labelTables = comp.roots.length > 0 ? comp.roots : [...comp.tables];
    return labelTables.join(', ');
}

// ---------------------------------------------------------------------------
// Master-list row label: root(s) + every member table, truncated (task 6a
// fix 2). Root(s) come first (stable, matches the modal header), followed by
// every other member table sorted alphabetically for determinism.
// ---------------------------------------------------------------------------

function orderedMemberTables(comp) {
    const roots = comp.roots.length > 0 ? comp.roots : [];
    const rest = [...comp.tables].filter((t) => !roots.includes(t)).sort();
    return roots.length > 0 ? [...roots, ...rest] : rest;
}

// Character budget for the joined "root, member, member..." string on a
// master-list row before we truncate. 50 keeps most real-world trees
// (root + a handful of short table names) fully visible while still
// truncating pathological long ones. Not pixel-perfect by design (per
// task brief) — just needs to reliably preserve the last table name.
const MEMBER_LIST_TRUNCATE_LENGTH = 50;

/**
 * Truncate a joined "table, table, table" string to at most `maxLength`
 * characters while GUARANTEEING the last table name in `tables` is fully
 * present at the end (never cut mid-word). Earlier entries in the string
 * may be cut mid-word if needed to make room — only the last one is
 * protected. This is a real string operation (not CSS text-overflow),
 * so the last table name is always in the rendered text, not merely
 * clipped by the container.
 */
function truncateMemberList(tables, maxLength = MEMBER_LIST_TRUNCATE_LENGTH) {
    const joined = tables.join(', ');
    if (joined.length <= maxLength) return joined;
    const last = tables[tables.length - 1];
    const suffix = `…, ${last}`;
    const headLength = Math.max(maxLength - suffix.length, 0);
    const head = joined.slice(0, headLength).replace(/,\s*$/, '');
    return `${head}${suffix}`;
}

function rowMemberListLabel(comp) {
    return truncateMemberList(orderedMemberTables(comp));
}

// ---------------------------------------------------------------------------
// Top-level component
// ---------------------------------------------------------------------------

export default function OwnershipRelationSection({ applicationId, groupId, selectedFieldIds, configuredFieldIds, onChange, apiHelper }) {
    const { theme } = useTheme();
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [openComponentId, setOpenComponentId] = useState(null);

    useEffect(() => {
        fetchFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, groupId]);

    const fetchFields = async () => {
        setLoading(true);
        try {
            const qs = groupId
                ? `?application_id=${applicationId}&group_id=${groupId}`
                : `?application_id=${applicationId}`;
            const resp = await apiHelper.apiCall(`/arcee/application/table-fields/fk-list/${qs}`);
            if (resp.ok) {
                const data = await resp.json();
                setFields(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error('Failed to load ownership relation fields', e);
        } finally {
            setLoading(false);
        }
    };

    // Explicit-target-state setter (NOT a naive toggle). A tree edge can
    // display as "enabled" without the field yet being in selectedFieldIds
    // (an unconfigured tree's implicit default). A naive toggle there would
    // see the field as "off" (absent from selectedFieldIds) and ADD it the
    // first time the user clicks to turn a default-enabled edge OFF —
    // inverting intent. Setting the target explicitly and only calling
    // onChange when it actually differs from real membership avoids that:
    // disabling a default-enabled-but-not-yet-included field is a no-op on
    // the real set, while enabling it for the first time really adds it.
    // (Hard-won fix from a prior review round — do not simplify to a toggle.)
    //
    // Also grows configuredFieldIds (durably, never shrinks it): a field the
    // admin has ever explicitly clicked — whether to enable or disable it —
    // is "configured" from now on, permanently, regardless of its current
    // enabled state. That's what lets a tree with zero enabled fields still
    // be told apart from a tree that was never touched.
    const setFieldEnabled = (fieldId, nextEnabled) => {
        const currentlyIncluded = selectedFieldIds.includes(fieldId);
        const currentlyConfigured = configuredFieldIds.includes(fieldId);
        if (nextEnabled === currentlyIncluded && currentlyConfigured) return;
        const nextSelectedFieldIds = nextEnabled
            ? (currentlyIncluded ? selectedFieldIds : [...selectedFieldIds, fieldId])
            : selectedFieldIds.filter((id) => id !== fieldId);
        const nextConfiguredFieldIds = currentlyConfigured ? configuredFieldIds : [...configuredFieldIds, fieldId];
        onChange(nextSelectedFieldIds, nextConfiguredFieldIds);
    };

    // Make "everything enabled by default" real the moment a never-configured
    // tree opens (design §5.2 "the graph is the editor"): the default-enabled
    // rendering must become the saved selection immediately, not a preview
    // that only sticks once the admin touches an edge. Marks every one of
    // these fields configured at the same time (see setFieldEnabled) — the
    // tree is fully defaulted in one atomic step, not "enabled now, maybe
    // configured later". A no-op (no onChange) if everything is already there.
    const enableFields = (fieldIds) => {
        const missingSelected = fieldIds.filter((id) => !selectedFieldIds.includes(id));
        const missingConfigured = fieldIds.filter((id) => !configuredFieldIds.includes(id));
        if (missingSelected.length === 0 && missingConfigured.length === 0) return;
        onChange(
            missingSelected.length ? [...selectedFieldIds, ...missingSelected] : selectedFieldIds,
            missingConfigured.length ? [...configuredFieldIds, ...missingConfigured] : configuredFieldIds,
        );
    };

    const components = useMemo(() => computeComponents(fields), [fields]);

    // related_table_name -> [fields whose FK points at it]. Used to find a
    // table's children when rendering the tree (a child is any table with a
    // field pointing at this one).
    const fieldsByRelatedTable = useMemo(() => {
        const map = {};
        for (const f of fields) {
            if (!f.related_table_name) continue;
            (map[f.related_table_name] = map[f.related_table_name] || []).push(f);
        }
        return map;
    }, [fields]);

    const filteredComponents = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return components;
        return components.filter((comp) => {
            if (componentLabel(comp).toLowerCase().includes(q)) return true;
            return [...comp.tables].some((t) => t.toLowerCase().includes(q));
        });
    }, [components, search]);

    const openComponent = components.find((c) => c.id === openComponentId) || null;

    const enabledCount = (comp) => {
        // A tree with any field in the durable configuredFieldIds is never
        // treated as "unconfigured", even if selectedFieldIds currently has
        // none of its fields (that means the admin deliberately disabled
        // every edge, not that the tree was never touched).
        const configured = comp.fields.some((f) => configuredFieldIds.includes(f.id));
        const enabled = configured
            ? comp.fields.filter((f) => selectedFieldIds.includes(f.id)).length
            : comp.fields.length;
        return { enabled, total: comp.fields.length };
    };

    return (
        <div className="mb-2">
            <label className="form-label text-muted small fw-bold mb-2">OWNERSHIP RELATION ACCESS</label>
            <div className="input-group mb-2" style={{ borderRadius: theme.inputRadius, overflow: 'hidden' }}>
                <span className="input-group-text bg-white border-end-0">
                    <i className="bi bi-search text-muted"></i>
                </span>
                <input
                    type="text"
                    className="form-control border-start-0 shadow-none"
                    placeholder="Search relations..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ fontSize: '0.9rem' }}
                />
            </div>
            <div className="card border-0 shadow-sm bg-light">
                <div className="card-body p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {!loading && filteredComponents.length === 0 && (
                        <small className="text-muted fst-italic p-2 d-block">No ownership relations found.</small>
                    )}
                    {filteredComponents.map((comp) => {
                        const { enabled, total } = enabledCount(comp);
                        return (
                            <div
                                key={comp.id}
                                data-testid={`tree-row-${comp.id}`}
                                role="button"
                                className="d-flex align-items-center justify-content-between py-2 px-3 mb-1 rounded hover-bg-white bg-white shadow-sm"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setOpenComponentId(comp.id)}
                            >
                                <span className="d-flex align-items-center">
                                    <i className="bi bi-diagram-3 text-primary me-2"></i>
                                    <span
                                        className="small fw-semibold"
                                        title={orderedMemberTables(comp).join(', ')}
                                    >
                                        {rowMemberListLabel(comp)}
                                    </span>
                                </span>
                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    {enabled}/{total} enabled
                                    <i className="bi bi-chevron-right ms-2"></i>
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="form-text small mt-2 d-flex align-items-center">
                <i className="bi bi-info-circle me-1"></i>
                Members will see rows whose FK chain resolves back to a row they own. Marking a column that points at a shared/reference table (not a genuine owner) over-exposes that table's rows to the whole group.
            </div>

            {openComponent && (
                <TreeModal
                    component={openComponent}
                    fieldsByRelatedTable={fieldsByRelatedTable}
                    selectedFieldIds={selectedFieldIds}
                    configuredFieldIds={configuredFieldIds}
                    onSetFieldEnabled={setFieldEnabled}
                    onEnableDefaults={enableFields}
                    onClose={() => setOpenComponentId(null)}
                    theme={theme}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Tree modal
// ---------------------------------------------------------------------------

/**
 * Large scrollable modal rendering one connected component as a real rooted,
 * branching tree. Root owner table(s) on the left; children (tables with an
 * FK pointing at the parent) branch to the right, recursively.
 */
function TreeModal({ component, fieldsByRelatedTable, selectedFieldIds, configuredFieldIds, onSetFieldEnabled, onEnableDefaults, onClose, theme }) {
    const compFields = component.fields;
    // Durable "has this tree ever been explicitly touched" signal, read
    // straight from the backend-persisted configuredFieldIds (see the
    // module-level doc comment at the top of this file) rather than guessed
    // from selectedFieldIds membership — a tree with zero enabled fields is
    // only "unconfigured" if none of its fields are in configuredFieldIds
    // either; otherwise it's a tree the admin deliberately emptied out.
    const compConfigured = compFields.some((f) => configuredFieldIds.includes(f.id));

    // Local per-edge overrides for instant visual feedback (the parent feeds
    // an updated selectedFieldIds back only via onChange, on the next render).
    const [overrides, setOverrides] = useState({});

    // Auto-persist defaults exactly once, on open, for a never-configured
    // tree (design §5.2). Ref + empty-deps guards against re-firing on every
    // render within a single mount of this modal. `compConfigured` itself is
    // derived from the durable configuredFieldIds prop, so it correctly
    // stays true across remounts (tree reopened, page revisited, full
    // reload) once a tree has been touched — no separate cross-remount
    // guard needed here anymore.
    const didInitRef = useRef(false);
    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;
        if (compConfigured) return;
        const defaultedIds = compFields.map((f) => f.id);
        onEnableDefaults(defaultedIds);
        // Instant visual feedback via the same local-override mechanism the
        // edge-click handler uses below — don't depend on the parent's
        // onChange -> selectedFieldIds/configuredFieldIds round trip landing
        // before the next paint (it may be async, or in a non-controlled
        // caller may never land at all).
        setOverrides((prev) => {
            const next = { ...prev };
            for (const id of defaultedIds) next[id] = true;
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isEdgeEnabled = (f) => {
        if (Object.prototype.hasOwnProperty.call(overrides, f.id)) return overrides[f.id];
        // Unconfigured tree defaults to all-enabled.
        if (!compConfigured) return true;
        return selectedFieldIds.includes(f.id);
    };

    const handleEdgeClick = (f) => {
        const nextEnabled = !isEdgeEnabled(f);
        setOverrides((prev) => ({ ...prev, [f.id]: nextEnabled }));
        onSetFieldEnabled(f.id, nextEnabled);
    };

    const roots = component.roots.length > 0 ? component.roots : [...component.tables];
    const rootsAreReal = component.roots.length > 0;

    return (
        <div
            className="modal d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content shadow-lg border-0" style={{ borderRadius: theme.cardRadius }}>
                    <div className="modal-header border-bottom py-3 px-4">
                        <h5 className="modal-title fw-bold d-flex align-items-center">
                            <i className="bi bi-diagram-3 text-primary me-2"></i>
                            Ownership tree: {componentLabel(component)}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                    </div>
                    <div className="modal-body p-4" style={{ overflow: 'auto', maxHeight: '75vh' }}>
                        <p className="small text-muted d-flex align-items-start mb-4">
                            <i className="bi bi-info-circle me-2 mt-1"></i>
                            {/* Single flex item: every direct child of a flex container (d-flex above)
                                becomes its own flex item, including bare text nodes — with the icon and
                                spans as flex siblings that broke around the icons instead of wrapping as
                                one sentence. Wrapping the whole sentence in one span keeps it as ordinary
                                inline content that flows and wraps normally. */}
                            <span>
                                Members will see rows whose FK chain resolves back to a row they own. Click any edge to
                                enable (<span className="mx-1">▶</span>) or disable (<span className="mx-1">✕</span>) that
                                relation. Tables past a disabled edge become unreachable and are dimmed.
                            </span>
                        </p>
                        {!rootsAreReal && (
                            <div className="alert alert-warning small py-2 px-3 mb-3">
                                This relation group has no single owner table (an inconsistent, not-yet-saved
                                snapshot). Showing all tables in the group.
                            </div>
                        )}
                        <div className="d-flex flex-column" style={{ gap: '1.5rem', minWidth: 'fit-content' }}>
                            {roots.map((rootTable) => (
                                <TreeNode
                                    key={rootTable}
                                    table={rootTable}
                                    parentEdge={null}
                                    parentReached
                                    visited={[]}
                                    renderChildren={rootsAreReal}
                                    fieldsByRelatedTable={fieldsByRelatedTable}
                                    isEdgeEnabled={isEdgeEnabled}
                                    onEdgeClick={handleEdgeClick}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="modal-footer border-top py-3 px-4">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Recursive tree node
// ---------------------------------------------------------------------------

const NODE_BOX_STYLE = {
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    padding: '0.35rem 0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid #dee2e6',
    background: '#fff',
    fontSize: '0.85rem',
    fontWeight: 600,
};

/**
 * Renders `table` and, to its right, a vertically-stacked column of its
 * children (tables whose FK points at `table`), each reached via a recursive
 * call. The connector to a child carries that child's edge toggle (▶ / ✕).
 *
 * Reachability propagates top-down: a node is reached if it's a root, or its
 * edge to its parent is enabled AND its parent is reached. `visited` is the
 * set of tables on the current path — a defensive cycle guard (the backend
 * rejects cyclical configs, so this should never trigger in practice, but it
 * must not infinite-loop if it does).
 */
function TreeNode({ table, parentEdge, parentReached, visited, renderChildren = true, fieldsByRelatedTable, isEdgeEnabled, onEdgeClick }) {
    const reached = parentEdge ? parentReached && isEdgeEnabled(parentEdge) : true;
    const onPath = visited.includes(table);
    const children = !onPath && renderChildren ? fieldsByRelatedTable[table] || [] : [];
    const nextVisited = [...visited, table];

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {parentEdge && (
                    <Edge
                        edge={parentEdge}
                        enabled={isEdgeEnabled(parentEdge)}
                        onClick={() => onEdgeClick(parentEdge)}
                    />
                )}
                <div
                    data-testid={`tree-node-${table}`}
                    data-unreached={!reached}
                    style={{ ...NODE_BOX_STYLE, opacity: reached ? 1 : 0.4, borderStyle: reached ? 'solid' : 'dashed' }}
                >
                    {table}
                    {onPath && <span className="text-danger ms-1" title="cycle">↺</span>}
                </div>
            </div>
            {children.length > 0 && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                        paddingLeft: '1.5rem',
                        marginLeft: '0.75rem',
                        borderLeft: '2px solid #dee2e6',
                    }}
                >
                    {children.map((f) => (
                        <TreeNode
                            key={f.id}
                            table={f.table_name}
                            parentEdge={f}
                            parentReached={reached}
                            visited={nextVisited}
                            renderChildren={renderChildren}
                            fieldsByRelatedTable={fieldsByRelatedTable}
                            isEdgeEnabled={isEdgeEnabled}
                            onEdgeClick={onEdgeClick}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * A single clickable edge between a parent (left) and child (right). Arrow
 * (▶) when enabled, cross (✕) when disabled. The whole element is the toggle.
 */
function Edge({ edge, enabled, onClick }) {
    return (
        <span
            data-testid={`tree-edge-${edge.id}`}
            data-enabled={enabled}
            role="button"
            title={`${edge.table_name}.${edge.name} → ${edge.related_table_name}`}
            onClick={onClick}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.15rem',
                cursor: 'pointer',
                userSelect: 'none',
                color: enabled ? '#0d6efd' : '#dc3545',
                fontWeight: 700,
                margin: '0 0.35rem',
                fontSize: '0.9rem',
            }}
        >
            <span style={{ letterSpacing: '-1px' }}>──</span>
            <span>{enabled ? '▶' : '✕'}</span>
            <span style={{ letterSpacing: '-1px' }}>──</span>
        </span>
    );
}
